// src/api/rentToOwn.js
// FT-173 REQ-03: единственная точка входа для /api/v1/rent_to_own/*. Компоненты
// модуля вызывают axios только через эти обёртки — не напрямую this.axios для
// путей rent_to_own, чтобы разбор кодов ошибок (см. parseRentToOwnError ниже) не
// разъехался по местам использования.
//
// axios передаётся вызывающей стороной (обычно this.axios компонента, тот же
// инстанс, что и у остального приложения) — интерсептор авторизации уже висит на
// Vue.axios (main.js), переустанавливать его здесь не нужно. DI, а не собственный
// import axios, — специально: юнит-тесты мокают axios ручным объектом (jest.fn и
// vi.mock в этом сьюте не работают, mochapack/Mocha), конвенция
// tests/unit/bookings/BookingAddonsPanel.spec.js:27-59.
import { reportModuleFailure } from "@/utils/moduleFailure";

const BASE = "/api/v1/rent_to_own";

// REQ-03: закрытый список кодов, которые превращаются в ключ i18n
// `rent_to_own.errors.<code>`. Список — всё, что реально отдаёт api модуля
// (app/controllers/api/v1/rent_to_own/* и concerns rent_to_own_access /
// rent_to_own_managed_booking): отказы доступа, бизнес-отказы, гонка на
// уникальном индексе (conflict), 404. `validation_failed` разбирается
// отдельно (поля и коды, см. validationMessages ниже), но тоже несёт общий
// ключ — для строки над кнопками.
export const RENT_TO_OWN_ERROR_CODES = Object.freeze([
  "paywall",
  "forbidden",
  "module_disabled",
  "invalid_transition",
  "car_unavailable",
  "fee_already_paid",
  "product_in_use",
  "managed_by_contract",
  "conflict",
  "reason_required",
  "not_waivable",
  "not_found",
]);

function unwrap(promise) {
  return promise.then((response) => response.data);
}

// REQ-03, CTR-05: разбор ошибки модуля. Вызывается ДО this.setError —
// setErrorMixin умеет опознавать только 422/406 и 404 с узнанной строкой
// (src/mixins/setErrorMixin.js:97-114); без этого разбора 403 (`forbidden`) и
// остальные бизнес-коды модуля ушли бы в общую ветку: красный тост и Rollbar на
// ожидаемый отказ, а не баг.
//
// Возвращает:
//   - { code, status, details, codes, i18nKey } для validation_failed
//     (codes — машинные коды по полям, api FT-173; details — английский
//     текст Rails, наружу не показывается, берётся только набор полей);
//   - { code, status, i18nKey } для остальных кодов из RENT_TO_OWN_ERROR_CODES;
//   - null, если код не опознан (нет response, код вне списка, сетевая ошибка,
//     500 без кода) — тогда вызывающая сторона передаёт ошибку в
//     reportRentToOwnFailure (ниже).
export function parseRentToOwnError(error) {
  const response = error && error.response;
  const status = response ? response.status : null;
  const data = response ? response.data : null;
  const code = data && typeof data === "object" ? data.error : null;
  if (!code || typeof code !== "string") return null;

  if (code === "validation_failed") {
    const codes = data.codes && typeof data.codes === "object" ? data.codes : null;
    return { code, status, details: (data && data.details) || {}, codes, i18nKey: `rent_to_own.errors.${code}` };
  }
  if (code === "invalid_transition") {
    const reason = completionRefusalReason(data.details);
    if (reason) return { code, status, i18nKey: `rent_to_own.errors.${code}`, reason, details: data.details };
  }
  if (RENT_TO_OWN_ERROR_CODES.includes(code)) {
    return { code, status, i18nKey: `rent_to_own.errors.${code}` };
  }
  return null;
}

// Часть F (USER 2026-09-25): отказ «Завершить» — не «договор изменился», а
// причина в details.reason (api RentToOwn::Completion): не всё оплачено
// (unpaid_installment_ids) или у машины другие активные бронирования
// (other_active_booking_ids). invalid_transition без reason — по-прежнему
// «Договор изменился» и перечитывание.
export const COMPLETION_REFUSAL_REASONS = Object.freeze(["unpaid_installments", "car_has_other_bookings"]);

function completionRefusalReason(details) {
  const reason = details && typeof details === "object" ? details.reason : null;
  return COMPLETION_REFUSAL_REASONS.includes(reason) ? reason : null;
}

// Замечание USER 2026-09-25 («has already been taken» на шаге 2): текст
// ошибки у поля — только локализованный. Коды валидации Rails (errors.details)
// → общий шаблон; там, где общий шаблон оператору непонятен, — особый текст
// по паре «поле + код». Карты литеральные (dynamicKeys.spec.js).
const VALIDATION_KEYS = Object.freeze({
  blank: "rent_to_own.validation.blank",
  taken: "rent_to_own.validation.taken",
  inclusion: "rent_to_own.validation.inclusion",
  invalid: "rent_to_own.validation.invalid",
  not_a_number: "rent_to_own.validation.not_a_number",
  not_an_integer: "rent_to_own.validation.not_an_integer",
  greater_than: "rent_to_own.validation.greater_than",
  greater_than_or_equal_to: "rent_to_own.validation.greater_than_or_equal_to",
  less_than: "rent_to_own.validation.less_than",
  less_than_or_equal_to: "rent_to_own.validation.less_than_or_equal_to",
  too_long: "rent_to_own.validation.too_long",
  too_short: "rent_to_own.validation.too_short",
});

// Особые тексты. Уникальности модели RentToOwn::Contract: номер договора в
// компании (uniqueness) и один живой договор на машину
// (car_has_no_other_live_contract → :car, :taken). belongs_to без записи —
// :blank («must exist»). Аванс ≥ выкупной цены api пишет строкой-сообщением
// (errors.add(:advance_amount, "must be less than buyout price")) — её код
// равен этой строке. Пустое тело запроса — поле `contract`, :blank.
const SPECIAL_VALIDATION_KEYS = Object.freeze({
  car: Object.freeze({ taken: "rent_to_own.validation.car_taken", blank: "rent_to_own.validation.car_blank" }),
  car_id: Object.freeze({ blank: "rent_to_own.validation.car_blank" }),
  client: Object.freeze({ blank: "rent_to_own.validation.client_blank" }),
  client_id: Object.freeze({ blank: "rent_to_own.validation.client_blank" }),
  product: Object.freeze({ blank: "rent_to_own.validation.product_blank" }),
  product_id: Object.freeze({ blank: "rent_to_own.validation.product_blank" }),
  number: Object.freeze({ taken: "rent_to_own.validation.number_taken" }),
  start_on: Object.freeze({ blank: "rent_to_own.validation.start_on_blank" }),
  contract: Object.freeze({ blank: "rent_to_own.validation.body_missing" }),
  advance_amount: Object.freeze({ "must be less than buyout price": "rent_to_own.validation.advance_below_buyout" }),
});

export const VALIDATION_FALLBACK_KEY = "rent_to_own.validation.check_field";

function own(map, key) {
  return map && typeof key === "string" && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null;
}

// Код ошибки поля → ключ локали: особый, иначе общий, иначе «Проверьте поле».
export function validationKey(field, code) {
  return own(own(SPECIAL_VALIDATION_KEYS, field), code) || own(VALIDATION_KEYS, code) || VALIDATION_FALLBACK_KEY;
}

// parsed (validation_failed) → { поле: [локализованный текст] }. t — функция
// перевода ((key, params) => string), обычно (k, p) => vm.$t(k, p). Без codes
// (api до FT-173) английский details наружу не отдаётся: у каждого поля из
// details — общий «Проверьте поле».
export function validationMessages(t, parsed) {
  const result = {};
  if (!parsed) return result;
  const codes = parsed.codes;
  if (codes && Object.keys(codes).length) {
    Object.keys(codes).forEach((field) => {
      const entries = Array.isArray(codes[field]) ? codes[field] : [];
      const messages = entries.map((entry) => {
        const code = entry && entry.error;
        const key = validationKey(field, code);
        return entry && entry.count != null ? t(key, { count: entry.count }) : t(key);
      });
      result[field] = messages.length ? messages : [t(VALIDATION_FALLBACK_KEY)];
    });
    return result;
  }
  Object.keys(parsed.details || {}).forEach((field) => {
    result[field] = [t(VALIDATION_FALLBACK_KEY)];
  });
  return result;
}

// Есть ли у поля данный код (car + taken → ссылка на договор, который держит
// машину).
export function hasValidationCode(parsed, field, code) {
  const entries = parsed && parsed.codes && Array.isArray(parsed.codes[field]) ? parsed.codes[field] : [];
  return entries.some((entry) => entry && entry.error === code);
}

// REQ-01: paywall и forbidden — оба «нет доступа», но экран разный (апсейл против
// текста «Нет доступа к модулю»); вызывающая сторона решает по parsed.code, этот
// хелпер — только общий признак «это не баг, а отказ доступа».
export function isAccessDenied(parsed) {
  return !!parsed && (parsed.code === "paywall" || parsed.code === "forbidden" || parsed.code === "module_disabled");
}

// REQ-01: общий catch-хендлер отказа доступа, повторявшийся в каждом
// компоненте модуля (ContractsList/ContractPage/Products/ContractWizard) —
// проставляет vm.paywallLocked/vm.forbidden и возвращает true, если ошибка
// была одной из них (вызывающая сторона тогда завершает catch через return).
export function applyAccessDenied(vm, parsed) {
  if (!isAccessDenied(parsed)) return false;
  if (parsed.code === "paywall") vm.paywallLocked = true;
  else if (parsed.code === "module_disabled") vm.moduleDisabled = true; // FT-194: переключатель филиала выключен
  else vm.forbidden = true;
  return true;
}

// Замечание USER 2026-09-25 («исправляй всё»): ни один текст сервера не
// показывается как есть. Отказ с кодом модуля — локализованный текст (warning,
// без Rollbar — это не баг). Прочее (500, сеть) — Rollbar через $reportError
// и свой тост с локализованным текстом вызывающей стороны.
//
// Глобальный setError (setErrorMixin) здесь НЕ зовётся (часть H, ревью rev4
// F1): он всегда пишет в vm.error строку сервера, и там, где вызывающая
// сторона не перезаписывает vm.error (ошибка вкладки, сохранения, удаления),
// шаблон v-if="error" заменял ею весь экран. Гварды setError повторены:
// «сессия мертва» и отказ оператора от крупной суммы — не сбой (без Rollbar и
// тоста), 422/406 без кода модуля — ожидаемый отказ (без Rollbar), 429 — лимит
// частоты: warning с временем ожидания без Rollbar, и на странице — эта причина,
// а не общий текст; сеть — warning в Rollbar и без тоста (её показывает
// глобальный баннер).
// Возвращает текст, который вызывающая сторона показывает на странице.
export function reportRentToOwnFailure(vm, err, text, { silent = false } = {}) {
  return reportModuleFailure(vm, err, { text, silent, parse: parseRentToOwnError, errorTitle: () => vm.$t("errors.error") });
}

// FT-173 решение USER D1 (REQ-01, REQ-11): модуль открывает ТОЛЬКО услуга
// rent_to_own в plan.included_services — активный trial его не открывает
// (в отличие от общего includedInPlan компонентов, который для прочих
// услуг считает trial и не меняется). Одним предикатом управляются пункт
// меню, кнопки «Оформить выкуп» и апсейл PlanGate на страницах модуля.
export const RENT_TO_OWN_SERVICE = "rent_to_own";

function includedServices(mainCompany) {
  const plan = mainCompany && mainCompany.plan;
  return plan && Array.isArray(plan.included_services) ? plan.included_services : null;
}

// Меню и кнопки: пока план не загружен — не показываем.
export function rentToOwnInPlan(mainCompany) {
  const services = includedServices(mainCompany);
  return !!services && services.includes(RENT_TO_OWN_SERVICE);
}

// FT-194 REQ-02, REQ-06: модуль доступен филиалу — услуга в плане И переключатель филиала. Меню и кнопки — по нему.
export function rentToOwnEnabled(mainCompany, company) {
  return rentToOwnInPlan(mainCompany) && !!company && company.rent_to_own_on === true;
}

// Филиал из стора ({ company } в getCompany) или null, пока не загружен.
export function storeCompany(getters) {
  return (getters && getters.getCompany && getters.getCompany.company) || null;
}

// То же правило по геттерам стора — для меню и кнопок «Оформить выкуп».
export function rentToOwnStoreEnabled(getters) {
  return rentToOwnEnabled(getters && getters.getMainCompany, storeCompany(getters));
}

// FT-194: страница — «модуль выключен» только когда план И филиал загружены (у филиала есть id), услуга есть,
// переключателя нет. Пока что-то не загружено — false: сообщение не мигает на входе (как rentToOwnPlanLocked).
export function rentToOwnModuleOff(mainCompany, company) {
  return rentToOwnInPlan(mainCompany) && !!company && company.id != null && company.rent_to_own_on !== true;
}

// Страница: апсейл только когда план загружен и услуги в нём нет — пока плана
// в сторе нет, PlanGate тоже не запирает (иначе апсейл мигал бы на входе).
export function rentToOwnPlanLocked(mainCompany) {
  const services = includedServices(mainCompany);
  return !!services && !services.includes(RENT_TO_OWN_SERVICE);
}

export const rentToOwnApi = {
  // FT-194 CTR-03: переключатель модуля в филиале сотрудника.
  updateSettings: (axios, enabled) => unwrap(axios.patch(`${BASE}/settings`, { enabled })),
  // REQ-04 список договоров; REQ-17 фильтр booking_id (используется и панелью
  // договора в карточке бронирования, REQ-12).
  listContracts: (axios, params) => unwrap(axios.get(`${BASE}/contracts`, { params })),
  // REQ-05 чтение договора.
  getContract: (axios, id) => unwrap(axios.get(`${BASE}/contracts/${id}`)),
  // REQ-19, CTR-02: вкладка «Платежи».
  listPayments: (axios, id) => unwrap(axios.get(`${BASE}/contracts/${id}/payments`)),
  // REQ-05 вкладка «История».
  listEvents: (axios, id) => unwrap(axios.get(`${BASE}/contracts/${id}/events`)),
  // REQ-09 мастер: создание черновика.
  createContract: (axios, attrs) => unwrap(axios.post(`${BASE}/contracts`, { contract: attrs })),
  // REQ-05, REQ-09: правка черновика мастером (только draft — гейт на сервере).
  updateContract: (axios, id, attrs) => unwrap(axios.patch(`${BASE}/contracts/${id}`, { contract: attrs })),
  // REQ-09 шаг 4, REQ-24, CTR-08: предпросмотр графика несохранённого договора,
  // то же тело {contract: attrs}, что create — без записи в БД.
  // contractId — предпросмотр правимого (уже сохранённого) черновика: api
  // накладывает условия на сам черновик в памяти, и проверка «одна живая
  // сделка на машину» не считает его чужим. Не черновик — 409 invalid_transition.
  previewDraft: (axios, attrs, contractId = null) =>
    unwrap(axios.post(`${BASE}/contracts/preview`, contractId ? { contract_id: contractId, contract: attrs } : { contract: attrs })),
  // REQ-06.
  previewSchedule: (axios, id) => unwrap(axios.post(`${BASE}/contracts/${id}/preview_schedule`)),
  activateContract: (axios, id) => unwrap(axios.post(`${BASE}/contracts/${id}/activate`)),
  cancelContract: (axios, id) => unwrap(axios.post(`${BASE}/contracts/${id}/cancel`)),
  // REQ-07.
  earlyPayoffQuote: (axios, id) => unwrap(axios.post(`${BASE}/contracts/${id}/early_payoff_quote`)),
  earlyPayoff: (axios, id) => unwrap(axios.post(`${BASE}/contracts/${id}/early_payoff`)),
  settlementQuote: (axios, id) => unwrap(axios.post(`${BASE}/contracts/${id}/settlement_quote`)),
  // reason — плоским полем тела (api читает params[:reason], не вложенным контрактом).
  terminateContract: (axios, id, reason) => unwrap(axios.post(`${BASE}/contracts/${id}/terminate`, { reason })),
  completeContract: (axios, id) => unwrap(axios.post(`${BASE}/contracts/${id}/complete`)),
  waiveInstallment: (axios, contractId, installmentId) =>
    unwrap(axios.post(`${BASE}/contracts/${contractId}/installments/${installmentId}/waive`)),
  // REQ-08, REQ-12: раскладка платежа (после payment-saved и «Повторить раскладку»).
  allocatePayment: (axios, id) => unwrap(axios.post(`${BASE}/contracts/${id}/allocate`)),
  // REQ-10 продукты.
  listProducts: (axios) => unwrap(axios.get(`${BASE}/products`)),
  createProduct: (axios, attrs) => unwrap(axios.post(`${BASE}/products`, { product: attrs })),
  updateProduct: (axios, id, attrs) => unwrap(axios.patch(`${BASE}/products/${id}`, { product: attrs })),
  deleteProduct: (axios, id) => unwrap(axios.delete(`${BASE}/products/${id}`)),
};
